import html2pdf from 'html2pdf.js'
import { format, getMinutes, formatDistanceToNowStrict } from 'date-fns'
import { isValidPhoneNumber } from 'libphonenumber-js'
import {
	getThemeColors,
	formatCurrency as formatCurrencyPreview,
	calculateDefaultSecondaryColor,
	getCommunityColors,
} from '~/stan-vue-shared/components/utils.js'
import { configSettings } from '~/plugins/config-settings.js'
import constants from './constants.js'

const STRIPE_ZERO_DECIMAL_CURRENCIES = [
	'BIF',
	'CLP',
	'DJF',
	'GNF',
	'JPY',
	'KMF',
	'KRW',
	'MGA',
	'PYG',
	'RWF',
	'UGX',
	'VND',
	'VUV',
	'XAF',
	'XOF',
	'XPF',
]

const TRAFFIC_SOURCE_TO_TITLE = {
	'android-app://com.google.android.gm': 'Google Android App',
	'google.com': 'Google',
}

export default {
	// Get Object Size
	objectSize(obj) {
		let size = 0
		let key
		for (key in obj) {
			if (obj.hasOwnProperty(key)) size++
		}
		return size
	},
	isTablet() {
		const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
		if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || width < 992) {
			return true
		}
		return false
	},
	isMobile() {
		const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
		if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || width < 768) {
			return true
		}
		return false
	},
	isPhone() {
		if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
			return true
		}
		return false
	},
	isMobileWidthOrLess() {
		const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
		if (width < 768) {
			return true
		}
		return false
	},
	stringLength(str) {
		return str ? str.length : 0
	},
	validateEmail(email) {
		const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
		return re.test(String(email).toLowerCase())
	},
	maskEmail(email) {
		const atIndex = email.indexOf('@')
		if (atIndex === -1) return email

		const username = email.slice(0, atIndex)
		const domain = email.slice(atIndex)

		if (username.length > 2) {
			const masked = username[0] + '*'.repeat(username.length - 2) + username[username.length - 1]
			return masked + domain
		}

		return email
	},
	compareStrings(firstString, secondString) {
		if (firstString === secondString) {
			return true
		}
		return false
	},
	validateUsername(username) {
		const re = /^[a-zA-Z0-9_-]+$/
		return re.test(username)
	},
	validateUsernameLength(username) {
		return username && username.length > 4 && username.length < 49
	},
	validateMFACodeFormat(code) {
		const re = /^\d{6}$/
		return re.test(code)
	},
	validatePhone(phonenumber) {
		if (phonenumber && phonenumber.length) {
			return isValidPhoneNumber(phonenumber)
		}
		return false
	},
	isNullOrWhiteSpace(str) {
		return str === null || str === undefined || str.match(/^ *$/) !== null
	},
	isEmptyObject(obj) {
		return Object.keys(obj).length === 0 && obj.constructor === Object
	},
	isUndefined(data) {
		return typeof data === 'undefined'
	},
	formatDistance(timeInMs, addSuffix = true) {
		return formatDistanceToNowStrict(timeInMs, { addSuffix })
	},
	isFutureDate(date) {
		const dateCheck = format(date, constants.DATE_FORMAT.ISO_8601_DATE)
		const dateToday = format(new Date(), constants.DATE_FORMAT.ISO_8601_DATE)
		return dateCheck > dateToday
	},
	formatNumber(number, { locale = 'en-US', compact = false, decimals = 0 } = {}) {
		return Intl.NumberFormat(
			locale,
			compact ? { notation: 'compact', compactDisplay: 'short' } : { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
		).format(Math.abs(number))
	},
	formatCurrency(amount, currency = 'USD', locale = 'en-US', fractionDigits = 2, compact = false) {
		if (STRIPE_ZERO_DECIMAL_CURRENCIES.includes(currency)) {
			fractionDigits = 0
		}
		const options = {
			style: 'currency',
			currency,
			minimumFractionDigits: fractionDigits,
			maximumFractionDigits: fractionDigits,
		}

		if (compact) {
			options.notation = 'compact'
			options.compactDisplay = 'short'
		}

		return Intl.NumberFormat(locale, options).format(amount)
	},
	formatCurrencyPreview(amount, currency = 'USD', locale = 'en-US', longSymbolUs = false) {
		return formatCurrencyPreview(amount, currency, locale, longSymbolUs)
	},
	formatTime(d) {
		const date = new Date(d)
		const options = {
			timeStyle: 'short',
		}
		return date.toLocaleString('en-US', options)
	},
	formatDateShort(d, onlyDate = false, includeYear = false) {
		const date = new Date(d)
		const options = { month: 'short', day: 'numeric' }
		if (!onlyDate) {
			options.hour = 'numeric'
			options.minute = 'numeric'
		}
		if (date.getFullYear() !== new Date().getFullYear() || includeYear) {
			options.year = 'numeric'
		}
		return date.toLocaleString('en-US', options)
	},
	formatAsRelativeTime(timestamp, onlyDate = false, includeYear = false) {
		const diffInSeconds = Math.floor((new Date() - timestamp) / 1000)

		if (diffInSeconds < 5) return 'Just now'
		if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`

		const diffInMinutes = Math.floor(diffInSeconds / 60)
		if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`

		const diffInHours = Math.floor(diffInMinutes / 60)
		if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`

		return this.formatDateShort(timestamp, onlyDate, includeYear)
	},
	formatTitle(title) {
		if (title.length === 1) {
			return title.toUpperCase()
		}
		const withoutHyphens = title.replace('-', ' ')

		return `${withoutHyphens.charAt(0).toUpperCase()}${withoutHyphens.slice(1)}`
	},
	getCurrencySymbol(currency = 'USD', locale = 'en-US') {
		return Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 })
			.format(0)
			.replace(/\d/g, '')
			.trim()
	},
	isValidURL(url) {
		const urlRegex = /^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i
		return urlRegex.test(url)
	},
	isValidURLWithoutHttp(url) {
		const httpLessUrlRegex = /^(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i
		return httpLessUrlRegex.test(url)
	},
	autoAddHttps(url) {
		if (this.isValidURLWithoutHttp(url) && !this.isValidURL(url)) {
			url = `https://${url}`
		}
		if (url) {
			url = url.replaceAll(' ', '')
		}
		return url
	},
	createCenteredPopupWin(pageURL, pageTitle, popupWinWidth, popupWinHeight) {
		const left = (screen.width - popupWinWidth) / 2
		const top = (screen.height - popupWinHeight) / 4

		const authPopupWindow = window.open(
			pageURL,
			pageTitle,
			`resizable=yes, width=${popupWinWidth}, height=${popupWinHeight}, top=${top}, left=${left}`
		)

		if (!authPopupWindow) {
			// eslint-disable-next-line no-alert
			alert('Please allow popups for this website!')
		}
		return authPopupWindow
	},
	isProbable(probability) {
		return Math.random() < probability
	},
	getSocialHandle(value, domain) {
		if (value) {
			value = value.replaceAll(`https://www.${domain}`, '')
			value = value.replaceAll(`https://${domain}`, '')
			value = value.replaceAll(`http://www.${domain}`, '')
			value = value.replaceAll(`http://${domain}`, '')
			value = value.replaceAll(domain, '')

			if (value.indexOf('http://') === -1 && value.indexOf('https://') === -1) {
				value = value.replaceAll('/', '')
			}
		}
		return value
	},
	isValidHexColor(color) {
		const re = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/

		return re.test(color)
	},
	getDefaultTermsAndCondition() {
		return `
			<h1>Terms and Conditions</h1>
			<p><strong>PLEASE READ THESE TERMS OF USE CAREFULLY BEFORE USING THIS WEBSITE OR PURCHASING</strong></p>
			<p>By using the website, you signify your consent to these terms of use. If you do not agree to these Terms of Use, please do not use the website.</p>
			<p>Your access to and use of this website, as well as all related websites operated by (_____________) and (_____________) (which includes (_____________), among others) (collectively the “Site”)  is subject to the following terms and conditions (“Terms of Use”) and all applicable laws. By accessing and browsing the Site, you accept, without limitation or qualification, the Terms of Use and acknowledge that any other agreements between you and the Site are superseded and of no force or effect:</p>
			<ol><li><p>You agree that the Site itself, as well as all content, videos, training materials, products, services and/or other materials, made available on the Site by us or other third parties, as well as the look and feel of all of the foregoing, (collectively referred to as the “Content”) are maintained for your personal use and information by (_____________) (the “Company”) and are the property of the Company and/or its third party providers. You agree that such Company Content shall include all proprietary videos, HTML/CSS, Javascript, graphics, voice, and sound recordings, artwork, photos, documents, and text as well as all other materials included in the Site, excluding only the materials you provide. Subject to your compliance with these Terms of Use, the Company hereby grants you a limited license, which is non-exclusive, non-transferable, and non-sublicensable, to access, view, and use the Site solely for your personal purposes. No Company Content may be copied, reproduced, republished, uploaded, posted, transmitted, distributed, used for public or commercial purposes, or downloaded in any way unless written permission is expressly granted by the Company. Modification of the Content or use of the Content for any other purpose is a violation of the copyright and other proprietary rights of the Company, as well as other authors who created the materials, and may be subject to monetary damages and penalties. You may not distribute, modify, transmit or use the content of the Site or any Content, including any and all software, tools, graphics and/or sound files, for public or commercial purposes without the express written permission of the Company.</p></li>
			<li><p>All Content, such as text, data, graphics files, videos and sound files, and other materials contained in the Site, are copyrighted unless otherwise noted and are the property of the Company and/or a supplier to the Company. No such materials may be used except as provided in these Terms of Use.</p></li>
			<li><p>All trade names, trademarks, and images and biographical information of people used in the Company Content and contained in the Site, are either the property of, or used with permission by, the Company. The use of Content by you is strictly prohibited unless specifically permitted by these Terms of Use. Any unauthorized use of Content may violate the copyright, trademark, and other proprietary rights of the Company and/or third parties, as well as the laws of privacy and publicity, and other regulations and statutes. Nothing contained in this Agreement or in the Site shall be construed as granting, by implication or otherwise, any license or right to use any Trademark or other proprietary information without the express written consent of the Company or third party owner. The Company respects the copyright, trademark and all other intellectual property rights of others. The Company has the right, but has no obligation, to remove content and accounts containing materials that it deems, in its sole discretion, to be unlawful, offensive, threatening, libelous, defamatory, pornographic, obscene or otherwise objectionable or violates any party’s intellectual property or these Terms of Use. If you believe that your intellectual property rights are being violated and/or that any work belonging to you has been reproduced on the Site or in any Content in any way, you may notify Company at (_____________). Please provide your name and contact information, the nature of your work and how it is being violated, all relevant copyright and/or trademark registration information, the location/URL of the violation, and any other information you believe is relevant.</p></li>
			<li><p>While the Company uses reasonable efforts to include accurate and up-to-date information in the Site, the Company makes no warranties or representations as to its accuracy. The Company assumes no liability or responsibility for any errors or omissions in the content of the Site.</p></li>
			<li><p>When you register with the Company and/or this Site, you expressly consent to receive any notices, announcements, agreements, disclosures, reports, documents, communications concerning new products or services, or other records or correspondence from the Company. You consent to receive notices electronically by way of transmitting the notice to you by email.</p></li>
			<li><p>If you send comments or suggestions about the Site to the Company, including, but not limited to, notes, text, drawings, images, designs or computer programs, such submissions shall become, and shall remain, the sole property of the Company. No submission shall be subject to any obligation of confidence on the part of the Company. The Company shall exclusively own all rights to (including intellectual property rights thereto), and shall be entitled to unrestricted use, publication, and dissemination as to all such submissions for any purpose, commercial or otherwise without any acknowledgment or compensation to you.</p></li>
			<li><p>The Company shall use commercially reasonable efforts to restrict unauthorized access to our data and files. However no system whether or not password protected can be entirely impenetrable. You acknowledge that it may be possible for an unauthorized third party to access, view, copy, modify, or distribute the data and files you store using the Site. Use of the Site is completely at your own risk.</p></li>
			<li><p>The Company will not intentionally disclose any personally identifying information about you to third parties, except where the Company, in good faith, believes such disclosure is necessary to comply with the law or enforce these Terms of Use. By using the Site, you signify your acceptance of the Company’s Privacy Policy. If you do not agree with this Privacy Policy, in whole or part, please do not use this Site.</p></li>
			<li><p>NEITHER THE COMPANY NOR ANY OTHER PARTY INVOLVED IN CREATING, PRODUCING, OR MAINTAINING THE SITE AND/OR ANY CONTENT ON THE SITE SHALL BE LIABLE UNDER ANY CIRCUMSTANCES FOR ANY DIRECT, INCIDENTAL, CONSEQUENTIAL, INDIRECT, OR PUNITIVE DAMAGES ARISING OUT OF YOUR ACCESS TO OR USE OF THE SITE. WITHOUT LIMITING THE FOREGOING, ALL CONTENT ON THE SITE IS PROVIDED “AS IS” WITHOUT WARRANTY OF ANY KIND, EITHER EXPRESSED OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE. THE COMPANY DOES NOT WARRANT OR MAKE ANY REPRESENTATIONS REGARDING THE USE OF THE MATERIALS IN THE SITE, THE RESULTS OF THE USE OF SUCH MATERIALS, THE SUITABILITY OF SUCH MATERIALS FOR ANY USER’S NEEDS OR THE LIKELIHOOD THAT THEIR USE WILL MEET ANY USER’S EXPECTATIONS, OR THEIR CORRECTNESS, ACCURACY, RELIABILITY, OR CORRECTION. THE COMPANY LIKEWISE DOES NOT WARRANT OR MAKE ANY REPRESENTATIONS OR GUARANTEES THAT YOU WILL EARN ANY MONEY USING THE SITE OR THE COMPANY’S TECHNOLOGY OR SERVICES. YOU ACCEPT ALL RESPONSIBILITY FOR EVALUATING YOUR OWN EARNING POTENTIAL AS WELL AS EXECUTING YOUR OWN BUSINESS AND SERVICES. YOUR EARNING POTENTIAL IS ENTIRELY DEPENDENT ON YOUR OWN PRODUCTS, IDEAS, TECHNIQUES; YOUR EXECUTION OF YOUR BUSINESS PLAN; THE TIME YOU DEVOTE TO THE PROGRAM, IDEAS AND TECHNIQUES OFFERED AND UTILIZED; AS WELL AS YOUR FINANCES, YOUR KNOWLEDGE AND YOUR SKILL. SINCE THESE FACTORS DIFFER AMONG ALL INDIVIDUALS, THE COMPANY CANNOT AND DOES NOT WARRANT OR MAKE ANY REPRESENTATIONS OR GUARANTEES REGARDING YOUR SUCCESS OR INCOME LEVEL. THE COMPANY DOES NOT WARRANT THAT USE OF THE MATERIALS WILL BE UNINTERRUPTED OR ERROR FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT THIS SITE, THE CONTENT, AND/OR THE MATERIALS AVAILABLE ON THIS SITE ARE FREE FROM BUGS OR VIRUSES OR OTHER HARMFUL COMPONENTS. YOU ASSUME ALL RESPONSIBILITY FOR THE COST OF ALL NECESSARY REPAIRS OR CORRECTIONS. THE COMPANY SHALL NOT BE RESPONSIBLE FOR ANY PERFORMANCE OR SERVICE PROBLEMS CAUSED BY ANY THIRD PARTY WEBSITE OR THIRD PARTY SERVICE PROVIDER. ANY SUCH PROBLEM SHALL BE GOVERNED SOLELY BY THE AGREEMENT BETWEEN YOU AND THAT PROVIDER. Please note that the applicable jurisdiction may not allow the exclusion of implied warranties. Some of the above exclusions may thus not apply to you.</p></li>
			<li><p>THE COMPANY SHALL NOT BE RESPONSIBLE FOR ANY PERFORMANCE OR SERVICE PROBLEMS CAUSED BY ANY THIRD PARTY WEBSITE OR THIRD PARTY SERVICE PROVIDER (including, for example, your web service provider service, Stripe payment services, your software and/or any updates or upgrades to that software). ANY SUCH PROBLEM SHALL BE GOVERNED SOLELY BY THE AGREEMENT BETWEEN YOU AND THAT PROVIDER. THE COMPANY RESERVES THE RIGHT TO DETERMINE, IN ITS SOLE DISCRETION, WHETHER THE COMPANY IS RESPONSIBLE FOR ANY SUCH MALFUNCTION OR DISRUPTION. THE COMPANY ALSO RESERVES THE RIGHT TO LIMIT YOUR USE OF THE SITE AND/OR THE CONTENT OR TO TERMINATE YOUR ACCOUNT SHOULD THE COMPANY DETERMINE THAT YOU HAVE VIOLATED THESE TERMS OF USE, OR THAT YOU HAVE VIOLATED ANY OTHER RULES OR CONDITIONS OF THE COMPANY. THE COMPANY RESERVES THE RIGHT TO REFUSE ACCESS TO THE SITE AND/OR THE COMPANY’S CONTENT, PRODUCTS AND/OR SERVICES TO ANYONE IN ITS SOLE DISCRETION. THE COMPANY RESERVES THE RIGHT TO DETERMINE, IN ITS SOLE DISCRETION, WHETHER THE COMPANY IS RESPONSIBLE FOR ANY SUCH MALFUNCTION OR DISRUPTION. DUE TO THE NATURE OF THE DIGITAL PRODUCT, THERE ARE NO REFUNDS OFFERED.</p></li>
			<li><p>You agree that you will pay for all products you purchase through the Company. YOU ARE RESPONSIBLE FOR THE TIMELY PAYMENT OF ALL FEES AND FOR PROVIDING THE COMPANY WITH A VALID PAYMENT METHOD FOR PAYMENT OF ALL FEES. ALL SALES OF PRODUCTS ARE FINAL. Prices for products offered by the Company may change at any time, and the Company does not provide price protection or refunds in the event of a price reduction or promotional offering. If a product becomes unavailable following a transaction but prior to download, your sole remedy is a refund. If technical problems prevent or unreasonably delay delivery of your product, your exclusive and sole remedy is either replacement or refund of the price paid, as determined by the Company.</p></li>
			<li><p>IN NO EVENT SHALL THE COMPANY BE LIABLE FOR ANY SPECIAL, INCIDENTAL, INDIRECT, PUNITIVE, RELIANCE OR CONSEQUENTIAL DAMAGES, WHETHER FORESEEABLE OR NOT, INCLUDING, BUT NOT LIMITED TO, DAMAGE OR LOSS OF PROPERTY, EQUIPMENT, INFORMATION OR DATA, LOSS OF PROFITS, REVENUE OR GOODWILL, COST OF CAPITAL, COST OF REPLACEMENT SERVICES, OR CLAIMS FOR SERVICE INTERRUPTIONS OR TRANSMISSION PROBLEMS, OCCASIONED BY ANY DEFECT IN THE SITE, THE CONTENT, AND/OR RELATED MATERIALS, THE INABILITY TO USE SERVICES PROVIDED HEREUNDER OR ANY OTHER CAUSE WHATSOEVER WITH RESPECT THERETO, REGARDLESS OF THEORY OF LIABILITY. THIS LIMITATION WILL APPLY EVEN IF THE COMPANY HAS BEEN ADVISED OR IS AWARE OF THE POSSIBILITY OF SUCH DAMAGES.</p></li>
			<li><p>If your purchase includes calls with the Company, you are responsible for scheduling them. All bookings are non-refundable. If you have to cancel your call, please email (_____________) with the subject "CALL CANCELLATION- [date &amp; time of your call]" no less than 48 hours before your call. If you do not show up to your call or reschedule within 48 hours, you forfeit your call.</p></li>
			<li><p>You agree to indemnify and hold the Company and each of its directors, officers employees, and agents, harmless from any and all liabilities, claims, damages and expenses, including reasonable attorney’s fees, arising out of or relating to (i) your breach of this Agreement, (ii) any violation by you of law or the rights of any third party, (iii) any materials, information, works and/or other content of whatever nature or media that you post or share on or through the Site, (iv) your use of the Site or any services that the Company may provide via the Site, and (v) your conduct in connection with the Site or the services or with other users of the Site or the services. The Company reserves the right to assume the exclusive defense of any claim for which we are entitled to indemnification under this Section. In such an event, you shall provide the Company with such cooperation as is reasonably requested by the Company.</p></li>
			<li><p>The provisions of these Terms of Use are for the benefit of the Company, its subsidiaries, affiliates and its third party content providers and licensors, and each shall have the right to assert and enforce such provisions directly or on its own behalf.</p></li>
			<li><p>This agreement shall be governed by and construed in accordance with the laws of the State of Florida, without giving effect to any principles of conflicts of law. You further submit to the exclusive jurisdiction of the state and federal courts sitting in Orange County, Florida. If any provision of this agreement shall be unlawful, void, or for any reason unenforceable, then that provision shall be deemed severable from this agreement and shall not affect the validity and enforceability of any remaining provisions.</p></li>
			<li><p>These Terms of Use may be revised from time to time by updating this posting. You are bound by any such revisions and should therefore periodically visit this page to review the current Terms</p></li></ol>`
	},
	buildProductList(
		store,
		funnels,
		type = null,
		distinctLabels = false,
		excludeLinks = true,
		user = null,
		filterByPageStatus = -1,
		filterByMembershipPricing = false,
		filterByPaidProducts = false
	) {
		const defaultStoreNoName = { ...store, data: { name: '' } }
		const defaultStoreId = store?.store_id
		const stores = [defaultStoreNoName]
		Object.keys(funnels).forEach(k => {
			stores.push(funnels[k])
		})
		const productPages = []
		stores.forEach(s => {
			if (s) {
				let pages = s.pages || []
				if (type) {
					pages = pages.filter(p => p.type === type)
				}
				if (excludeLinks) {
					pages = pages.filter(p => ['link', 'stan-referral-link'].indexOf(p.type) < 0)
				}
				if (filterByPageStatus > -1) {
					pages = pages.filter(p => p.status === filterByPageStatus)
				}
				if (filterByMembershipPricing) {
					pages = pages.filter(p => this.isMembershipPricing(p))
				}
				if (filterByPaidProducts) {
					pages = pages.filter(p => this.isPaidProduct(p.data.product))
				}
				// Add funnel name to help distinguish
				const storeName = s?.data?.name
				pages = pages.map(p => ({ ...p, storeName }))
				productPages.push(...pages)
			}
		})
		if (distinctLabels) {
			const titles = productPages.map(p => p.data.product.title)
			return [...new Set(titles)].filter(p => !!p).map(p => ({ label: p }))
		}
		// note: for the icon image, the default store should use button image, and buttonless pages (funnel, landing pages) should use product image.
		const out = productPages.map(p => ({
			label: p.data.product.title,
			storeName: p.storeName,
			image: p.data.location === 'store' && p.store_id === defaultStoreId ? p.data.button.image : p.data.product.image,
			id: p.page_id,
			type: p.type,
			tag: `Purchased: ${p.data.product.title}`,
			productId: p.data.product.id,
			membershipId: p?.type === 'membership' ? p.data.product.id : p.data.product?.price?.membership_id,
			location: p.data.location,
			url: user ? `${configSettings.frontDomain}${user.username}/p/${p.slug}` : undefined,
		}))
		return out
	},
	getPageCurrency(user = null, pageData = null) {
		if (pageData?.currency_override && pageData.currency_override.enabled && pageData.currency_override.currency) {
			return pageData.currency_override.currency
		}

		return user.data.currency || 'USD'
	},
	isMembershipPricing(page) {
		const isMembershipProduct = page?.type === 'membership'
		const isMembershipPricing = page?.data?.product?.price?.interval
		return isMembershipProduct || isMembershipPricing
	},
	addThemeColorsToDocument(theme, primaryColorHex, secondaryColorHex, primaryFont) {
		const themeColors = getThemeColors(theme, primaryColorHex, secondaryColorHex)
		const communityColors = getCommunityColors(theme, primaryColorHex, secondaryColorHex)

		document.documentElement.style.setProperty('--stan-store-primary-color', themeColors.primaryColor)
		document.documentElement.style.setProperty('--stan-store-theme-primary-color', themeColors.primaryColor)
		document.documentElement.style.setProperty('--stan-store-secondary-color', themeColors.secondaryColor)
		document.documentElement.style.setProperty('--stan-store-theme-secondary-color', themeColors.secondaryColor)
		document.documentElement.style.setProperty('--stan-store-theme-tone1-color', themeColors.tone1)
		document.documentElement.style.setProperty('--stan-store-theme-tone2-color', themeColors.tone2)
		document.documentElement.style.setProperty('--stan-store-theme-tone3-color', themeColors.tone3)
		document.documentElement.style.setProperty('--stan-store-theme-derived1-color', themeColors.derived1)
		document.documentElement.style.setProperty('--stan-store-theme-derived2-color', themeColors.derived2)
		document.documentElement.style.setProperty('--stan-store-theme-derived3-color', themeColors.derived3)
		document.documentElement.style.setProperty('--stan-store-theme-primary-text-color', themeColors.primaryTextColor)
		document.documentElement.style.setProperty('--stan-store-theme-button-text-color', themeColors.buttonTextColor)
		document.documentElement.style.setProperty('--stan-store-theme-button-subtext-color', themeColors.subButtonTextColor)
		document.documentElement.style.setProperty('--stan-store-theme-secondary-text-color', themeColors.secondaryTextColor)
		document.documentElement.style.setProperty('--stan-store-theme-placeholder-text-color', themeColors.placeholderTextColor)
		document.documentElement.style.setProperty('--stan-community-primary-color', themeColors.primaryColor)
		document.documentElement.style.setProperty('--stan-community-secondary-color', communityColors.secondaryColor)

		if (primaryFont) document.documentElement.style.setProperty('--stan-store-theme-primary-font', primaryFont)

		return themeColors
	},
	parseQueryFilters(query, filters = {}) {
		const filteredFields = {}
		for (const queryArg in query) {
			const match = queryArg.match(/^([a-z-]*)\[([a-z-]*)\]$/)
			if (match && match.length === 3) {
				const fieldName = match[1]
				const operatorCode = match[2]
				const value = query[queryArg]
				let filterField = filteredFields[fieldName] || {}
				const filter = filters[fieldName] || {}
				if (filter.type === 'date') {
					if (!filterField) filterField = { operatorCode }
					if (operatorCode === 'gte') filterField.value = value
					else filterField.secondValue = value
				} else
					filterField = {
						operatorCode,
						value,
					}
				filteredFields[fieldName] = filterField
			}
		}
		return filteredFields
	},
	removeQueryFilters(query, fieldNames) {
		const newQueryObject = {}
		for (const queryArg in query) {
			const match = queryArg.match(/^([a-z-]*)\[([a-z-]*)\]$/)
			if (match && !(match.length === 3 && fieldNames.includes(match[1]))) {
				newQueryObject[queryArg] = query[queryArg]
			}
		}
		return newQueryObject
	},
	generateDelayFromSeconds(seconds) {
		const dayDivider = 60 * 60 * 24
		const hourDivider = 60 * 60
		const minuteDivider = 60

		if (seconds > 0) {
			if (seconds % dayDivider === 0) {
				return {
					unit: 'days',
					value: seconds / dayDivider,
				}
			}
			if (seconds % hourDivider === 0) {
				return {
					unit: 'hours',
					value: seconds / hourDivider,
				}
			}
		}
		if (seconds % minuteDivider === 0) {
			return {
				unit: 'minutes',
				value: seconds / minuteDivider,
			}
		}
		return null
	},
	getEmailDelayText(email) {
		const delay = this.generateDelayFromSeconds(email.delay)
		if (delay.value === 1) delay.unit = delay.unit.substr(0, delay.unit.length - 1)
		return delay.value === 0 ? 'Right away' : `${delay.value} ${delay.unit}`
	},
	isPaidProduct(product) {
		let price = 0
		if (product.price) price = product.price.sale_amount ? product.price.sale_amount : product.price.amount
		return price > 0
	},
	formatFlowEmailSubjectHtml(inputString) {
		let outputString = inputString.replace(/<[^>]+>/g, '')
		// We don't have a reliable way to accurately match {}, so falling back to matching exact strings
		outputString = outputString.replaceAll('{my_username}', '<span class="text-variable-name">My Username</span>')
		outputString = outputString.replaceAll('{my_name}', '<span class="text-variable-name">My Name</span>')
		outputString = outputString.replaceAll('{customer_name}', '<span class="text-variable-name">Customer Name</span>')
		outputString = outputString.replaceAll('{product_name}', '<span class="text-variable-name">Product Name</span>')

		return outputString
	},
	// Timezone offset in hours - from UTC+0
	getTimezoneOffset(timeZone) {
		const now = new Date()
		const tzString = now.toLocaleString('en-US', { timeZone })
		const localString = now.toLocaleString('en-US')
		const diff = (Date.parse(localString) - Date.parse(tzString)) / 3600000
		const offset = diff + now.getTimezoneOffset() / 60
		return -offset
	},
	// Will return true if `checkDate` is in the past. Will return false if in the future or the `checkDate` is invalid
	isDateInPast(checkDate, timeZone = null) {
		// Default to local timezone
		const timeZoneUse = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
		// offset current time to GMT 0 + offset to new timezone
		const timeZoneOffsetHours = -this.getTimezoneOffset() + this.getTimezoneOffset(timeZoneUse)

		// Current time offset
		const nowAdjusted = new Date(new Date().getTime() + timeZoneOffsetHours * 60000 * 60)

		// new Date(date), will create at UTC+0 then shift to our timezone so we need to offset back to get to midnight
		const checkDateAdjusted = new Date(new Date(checkDate).getTime() - this.getTimezoneOffset() * 60000 * 60)

		return checkDateAdjusted < nowAdjusted
	},
	parseGoogleMapsPlaceData(placeResultData) {
		/*
			Example object
			{
				"address_components": [
					{
						"long_name": "240",
						"short_name": "240",
						"types": [
							"street_number"
						]
					},
					{
						"long_name": "Richmond Street West",
						"short_name": "Richmond St W",
						"types": [
							"route"
						]
					},
					{
						"long_name": "Old Toronto",
						"short_name": "Old Toronto",
						"types": [
							"sublocality_level_1",
							"sublocality",
							"political"
						]
					},
					{
						"long_name": "Toronto",
						"short_name": "Toronto",
						"types": [
							"locality",
							"political"
						]
					},
					{
						"long_name": "Toronto",
						"short_name": "Toronto",
						"types": [
							"administrative_area_level_3",
							"political"
						]
					},
					{
						"long_name": "Toronto",
						"short_name": "Toronto",
						"types": [
							"administrative_area_level_2",
							"political"
						]
					},
					{
						"long_name": "Ontario",
						"short_name": "ON",
						"types": [
							"administrative_area_level_1",
							"political"
						]
					},
					{
						"long_name": "Canada",
						"short_name": "CA",
						"types": [
							"country",
							"political"
						]
					},
					{
						"long_name": "M5V",
						"short_name": "M5V",
						"types": [
							"postal_code_prefix",
							"postal_code"
						]
					}
				],
				"adr_address": "<span class=\"street-address\">240 Richmond St W</span>, <span class=\"locality\">Toronto</span>, <span class=\"region\">ON</span> <span class=\"postal-code\">M5V</span>, <span class=\"country-name\">Canada</span>",
				"formatted_address": "240 Richmond St W, Toronto, ON M5V, Canada",
				"geometry": {
					"location": {
						"lat": 43.6497141,
						"lng": -79.38887799999999
					},
					"viewport": {
						"south": 43.64827131970851,
						"west": -79.3902112802915,
						"north": 43.65096928029151,
						"east": -79.38751331970849
					}
				},
				"icon": "https://maps.gstatic.com/mapfiles/place_api/icons/v1/png_71/geocode-71.png",
				"name": "240 Richmond St W",
				"place_id": "ChIJU-jrKtA0K4gR-8JmJwWTF8Q",
				"types": [
					"premise"
				],
				"url": "https://maps.google.com/?q=240+Richmond+St+W,+Toronto,+ON+M5V,+Canada&ftid=0x882b34d02aebe853:0xc41793052766c2fb",
				"utc_offset": -240,
				"vicinity": "Old Toronto",
				"html_attributions": [],
				"utc_offset_minutes": -240
			}
		*/
		const address = {
			streetNumber: 0,
			route: null,
			city: null,
			state: null,
			postal_code: null,
			country: null,
		}

		if (placeResultData && placeResultData?.address_components) {
			const addressComponents = placeResultData.address_components

			address.streetNumber = addressComponents.find(a => a.types.includes('street_number'))?.long_name ?? ''

			address.route = {
				long: addressComponents.find(a => a.types.includes('route'))?.long_name,
				short: addressComponents.find(a => a.types.includes('route'))?.short_name,
			}

			const cityData = addressComponents.find(a => a.types.includes('locality')) || addressComponents.find(a => a.types.includes('postal_town'))

			address.city = {
				long: cityData?.long_name,
				short: cityData?.short_name,
			}

			address.state = {
				long: addressComponents.find(a => a.types.includes('administrative_area_level_1'))?.long_name,
				short: addressComponents.find(a => a.types.includes('administrative_area_level_1'))?.short_name,
			}

			address.postal_code = {
				long: addressComponents.find(a => a.types.includes('postal_code'))?.long_name,
				short: addressComponents.find(a => a.types.includes('postal_code'))?.short_name,
			}

			address.country = {
				long: addressComponents.find(a => a.types.includes('country'))?.long_name,
				short: addressComponents.find(a => a.types.includes('country'))?.short_name,
			}

			address.street = {
				long: address.streetNumber ? `${address.streetNumber} ${address.route.long}` : address.route?.long,
				short: address.streetNumber ? `${address.streetNumber} ${address.route.short}` : address.route?.short,
			}
		}

		return address
	},
	getImageURLFromPage(page) {
		if (!page || !page.data) {
			return null
		}
		if (page.data.location === 'landing') {
			return page.data.product?.image
		}
		return page.data.button?.image || page.data.product?.image
	},
	getStorePageListTitle(page) {
		if (page.data.button.layout === 'callout' || page.data.button.layout === 'preview') {
			return page.data.button.heading
		}
		return page.data.button.title
	},
	prepareQuestionAnswersForCSVExport(questionsAndAnswers) {
		if (!questionsAndAnswers) {
			return { csvColumns: {}, data: {} }
		}
		const csvColumns = {}
		const data = {}
		questionsAndAnswers.forEach(qa => {
			if (qa.answer !== undefined && qa.answer !== '') {
				// Expand any csv values so they do not affect CSV
				let answerValUse = qa.answer
				if (typeof qa.answer === 'string' || qa.answer instanceof String) {
					answerValUse = qa.answer.split(',').length > 1 ? qa.answer.split(',') : answerValUse
				}

				// Set csvColumn values
				csvColumns[qa.question] = {
					/* vue-json-excel - does dot expansion on strings, many questions use this for punctuation since 
					we already transformed the data here we just need to retrieve. That's what this callback does
					the library calls this function overirding any . expansion etc. that may be done. */

					callback: data => data[qa.question],
				}

				// Set data values
				if (Array.isArray(answerValUse)) {
					// To represent CSV values in csv wrap in quotes
					data[qa.question] = `"${answerValUse.join(',')}"`
				} else {
					data[qa.question] = answerValUse
				}
			}
		})

		return { csvColumns, data }
	},
	getFormattedTrafficTitle(trafficName) {
		if (TRAFFIC_SOURCE_TO_TITLE[trafficName]) {
			return TRAFFIC_SOURCE_TO_TITLE[trafficName]
		}
		return trafficName
	},

	downloadAsPDF(htmlContent, fileName) {
		// Create PDF from HTML content
		html2pdf()
			.from(htmlContent)
			.save(fileName)
	},

	displayTimeFormat(date) {
		const formatString = getMinutes(date) === 0 ? 'h aa' : 'h:mm aa'
		return format(date, formatString)
	},
	replaceBarDefaultLabel(data, oldKey, newKey) {
		if (data.hasOwnProperty(oldKey)) {
			const updatedLabelObject = { ...data, [newKey]: data[oldKey] }
			delete updatedLabelObject[oldKey]
			return updatedLabelObject
		}
		return data
	},
	getDefaultSecondaryColor(theme, hex) {
		return calculateDefaultSecondaryColor(theme, hex)
	},
	parseJWT(jwt) {
		const [header, payload, signature] = jwt.split('.')

		const parseData = toParse => {
			const base64 = toParse.replace(/-/g, '+').replace(/_/g, '/')
			const jsonPayload = decodeURIComponent(
				window
					.atob(base64)
					.split('')
					.map(c => {
						const charCode = `00${c.charCodeAt(0).toString(16)}`.slice(-2)
						return `%${charCode}`
					})
					.join('')
			)
			return JSON.parse(jsonPayload)
		}

		return {
			header: header ? parseData(header) : {},
			payload: payload ? parseData(payload) : {},
			signature,
		}
	},
	joinWithCommasAnd(stringsArr) {
		if (stringsArr.length === 0) return ''
		if (stringsArr.length === 1) return stringsArr[0]
		return `${stringsArr.slice(0, -1).join(', ')} and ${stringsArr[stringsArr.length - 1]}`
	},
	getAffiliateDefaultTermsAndCondition() {
		return `
			<h3>Affiliate Terms and Conditions</h3> 
			<p><strong>PLEASE READ THESE AFFILIATE TERMS OF USE CAREFULLY BEFORE PARTICIPATING IN THE AFFILIATE PROGRAM</strong></p> 
			<p>By participating in the Affiliate Program, you signify your consent to these terms of use. If you do not agree to these Terms of Use, please do not enroll in or participate in the Affiliate Program.</p> 
			<p>Your participation in the Affiliate Program operated by (_____________) (the “Company”) and all related websites operated by the Company (collectively, the “Site”) is subject to the following terms and conditions (the “Affiliate Terms of Use”) and all applicable laws. By registering for the Affiliate Program, you accept, without limitation or qualification, the Affiliate Terms of Use and acknowledge that any other agreements between you and the Company related to the Affiliate Program are superseded and of no force or effect.</p> 
			<ol> 
			<li> <p><strong>Affiliate Registration</strong>: To become an affiliate, you must complete the Company's registration process. The Company reserves the right to approve or reject any affiliate application in its sole discretion for any reason or no reason. By applying to the Affiliate Program, you represent and warrant that all information you provide to the Company is accurate and complete.</p> </li> 
			<li> <p><strong>Affiliate Responsibilities</strong>: As an affiliate, you agree to promote the Company's products and services in a lawful and ethical manner. You are solely responsible for your marketing efforts, including compliance with all applicable laws, regulations, and guidelines related to advertising and marketing, such as the Federal Trade Commission (FTC) guidelines regarding endorsements.</p> </li> 
			<li> <p><strong>Commission and Payment</strong>: Affiliates will earn a commission on sales of the Company's products generated through the affiliate's unique referral link, according to the commission structure provided by the Company. The Company reserves the right to adjust the commission rate or payment terms at any time. All sales are subject to approval and the commission will be paid only on approved sales. Commissions will be paid once the Company has received payment in full from the referred customer, and no refunds are pending.</p> </li> 
			<li> <p><strong>Referral Link and Tracking</strong>: You will be provided with a unique affiliate link to use in your promotional efforts. It is your responsibility to ensure the proper integration of the referral link. The Company is not liable for any tracking or reporting failures resulting from incorrect use of the referral link or other issues beyond the Company's control.</p> </li> 
			<li> <p><strong>Prohibited Activities</strong>: Affiliates are prohibited from engaging in any deceptive or fraudulent practices to generate sales. This includes, but is not limited to, using unsolicited email (spam), false advertising, or making unauthorized representations or claims about the Company's products or services. Affiliates may not use their affiliate link to make purchases for personal use.</p> </li> 
			<li> <p><strong>Intellectual Property</strong>: The Company grants you a limited, non-exclusive, revocable license to use the Company's trademarks, logos, and promotional materials for the sole purpose of promoting the Company's products. You may not modify or alter these materials in any way. The Company reserves all rights in its intellectual property and may revoke this license at any time for any reason.</p> </li> 
			<li> <p><strong>Termination</strong>: The Company reserves the right to terminate your participation in the Affiliate Program at any time, with or without cause. Upon termination, you must immediately cease using the Company's promotional materials and remove any references to the Company from your website or other promotional channels.</p> </li> 
			<li> <p><strong>Limitation of Liability</strong>: The Company shall not be liable for any direct, indirect, incidental, special, or consequential damages arising out of or related to your participation in the Affiliate Program, including but not limited to loss of profits or commissions, even if the Company has been advised of the possibility of such damages. The Company makes no representations or warranties regarding the Affiliate Program, including the operation or availability of the program or the potential for earning commissions.</p> </li> 
			<li> <p><strong>Confidentiality</strong>: You agree not to disclose or share any confidential information related to the Company or the Affiliate Program, including but not limited to commission rates, marketing strategies, or any information not publicly available, with any third party without the Company's express written consent.</p> </li> 
			<li> <p><strong>Governing Law</strong>: These Affiliate Terms of Use shall be governed by and construed in accordance with the laws of the State of (_____________), without regard to its conflict of law provisions. You agree to submit to the exclusive jurisdiction of the courts located within (_____________) for any disputes arising out of or relating to these Affiliate Terms of Use.</p> </li> 
			<li> <p><strong>Amendments</strong>: The Company reserves the right to modify or amend these Affiliate Terms of Use at any time. You are responsible for regularly reviewing these terms. Continued participation in the Affiliate Program following any changes to these terms constitutes your acceptance of the revised terms.</p> </li> 
			<li> <p><strong>Indemnification</strong>: You agree to indemnify, defend, and hold the Company and its officers, directors, employees, and agents harmless from any and all claims, liabilities, damages, losses, or expenses, including reasonable attorney's fees, arising out of or related to your participation in the Affiliate Program or any breach of these Affiliate Terms of Use.</p> </li> 
			</ol>`
	},
	isExistingUserImageDefault(imgLnk) {
		return imgLnk?.includes('no_user.png')
	},
	async copyToClipboard(textToCopy, successCallback = () => {}, failCallback = () => {}) {
		// Compatible with most browsers: https://caniuse.com/?search=navigator.clipboard
		try {
			await navigator.clipboard.writeText(textToCopy)
			return successCallback()
		} catch (err) {
			console.error('Failed to copy text: ', err)
		}

		// Fallback
		const textarea = document.createElement('textarea')
		textarea.value = textToCopy
		textarea.setAttribute('readonly', '')
		textarea.style.position = 'absolute'
		textarea.style.left = '-9999px'
		document.body.appendChild(textarea)
		textarea.select()

		let success = false
		try {
			success = document.execCommand('copy')
		} catch (err) {
			console.error('Failed to copy text using execCommand: ', err)
		}

		document.body.removeChild(textarea)

		if (success) {
			return successCallback()
		}
		return failCallback()
	},
	removePrefix(str, prefix) {
		if (str.startsWith(prefix)) {
			return str.slice(prefix.length) // Remove the prefix
		}
		return str // Return the original string if no prefix is found
	},

	getDeviceTypeFromInnerWidth() {
		if (this.isMobile()) return 'mobile'
		if (this.isTablet()) return 'tablet'
		return 'desktop'
	},

	getGDPRComplianceDocument(country) {
		const EU_GDPR_COUNTRY_CODES = [
			'AT',
			'BE',
			'BG',
			'HR',
			'CY',
			'CZ',
			'DK',
			'EE',
			'FI',
			'FR',
			'DE',
			'GR',
			'HU',
			'IE',
			'IT',
			'LV',
			'LT',
			'LU',
			'MT',
			'NL',
			'PL',
			'PT',
			'RO',
			'SK',
			'SI',
			'ES',
			'SE',
			'IS',
			'LI',
			'NO',
		]

		const UK_GDPR_COUNTRY_CODES = ['GB']

		if (EU_GDPR_COUNTRY_CODES.includes(country)) {
			return 'https://assets.stanwith.me/legal/EU_GDPR.pdf'
		}

		if (UK_GDPR_COUNTRY_CODES.includes(country)) {
			return 'https://assets.stanwith.me/legal/UK_GDPR.pdf'
		}

		return null
	},
	getDateXDaysAndYMonthsFromNow(x, y = 0, options = { month: 'long', day: 'numeric' }) {
		const date = new Date()

		// Add days first
		date.setDate(date.getDate() + x)
		const originalDay = date.getDate()

		// Add months
		date.setMonth(date.getMonth() + y)

		// Check if the day has changed (rolled over)
		if (date.getDate() !== originalDay) {
			// Set to last day of previous month
			date.setDate(0)
		}

		return date.toLocaleDateString('en-US', options)
	},
}
